import React, { useState } from 'react';
import { X, Plus, Trash2, UsersRound } from 'lucide-react';
import { toast } from 'sonner';

const COLORS = ['#7C5CFF', '#0F766E', '#0AA24F', '#E5387B', '#2D8DF0', '#F5B402', '#0E9C8E', '#4460F7'];
const inputCls = 'w-full px-3 py-2 border border-[#D5D7ED] rounded-md text-[14px] focus:outline-none focus:border-[#0A1551] focus:ring-2 focus:ring-[#0A1551]/10';

const newInvite = () => ({ uid: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `inv-${Math.random().toString(36).slice(2)}`, email: '', role: 'Member' });

const CreateTeamModal = ({ open, onClose, onCreate }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#7C5CFF');
  const [invites, setInvites] = useState([newInvite()]);
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const addInvite = () => setInvites((m) => [...m, newInvite()]);
  const removeInvite = (i) => setInvites((m) => m.filter((_, idx) => idx !== i));
  const updateInvite = (i, field, val) => setInvites((m) => m.map((it, idx) => (idx === i ? { ...it, [field]: val } : it)));

  const submit = async () => {
    if (!name.trim()) return;
    setLoading(true);
    try {
      const valid = invites.filter((m) => m.email.trim().includes('@')).map(({ email, role }) => ({ email, role }));
      await onCreate({ name: name.trim(), description: description.trim() || null, color, invites: valid });
      setName(''); setDescription(''); setInvites([newInvite()]);
      onClose();
    } catch (e) {
      toast.error('Failed to create team');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="create-team-modal">
      <div className="absolute inset-0 bg-[#0A1551]/40 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative w-full max-w-[560px] bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95">
        <div className="px-6 py-5 border-b border-[#E3E5F5] flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + '20' }}>
            <UsersRound className="w-5 h-5" style={{ color }} />
          </div>
          <div>
            <h2 className="text-[18px] font-bold text-[#0A1551]">Create a team</h2>
            <p className="text-[13px] text-[#6F76A7]">We email each person a link; they join automatically when they sign in with that address.</p>
          </div>
          <button onClick={onClose} className="ml-auto p-2 rounded-lg hover:bg-[#F5F6FB]" aria-label="Close">
            <X className="w-4 h-4 text-[#6F76A7]" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 max-h-[65vh] overflow-y-auto">
          <div>
            <label className="block text-[13px] font-semibold text-[#0A1551] mb-1.5">Team name</label>
            <input data-testid="team-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Marketing Team" className={inputCls} />
          </div>
          <div>
            <label className="block text-[13px] font-semibold text-[#0A1551] mb-1.5">Description (optional)</label>
            <input data-testid="team-description-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What this team is about" className={inputCls} />
          </div>
          <div>
            <label className="block text-[13px] font-semibold text-[#0A1551] mb-1.5">Team color</label>
            <div className="flex gap-2">
              {COLORS.map((c) => (
                <button key={c} onClick={() => setColor(c)} className={`w-7 h-7 rounded-full ring-2 ring-offset-2 transition-all ${color === c ? 'ring-[#0A1551]' : 'ring-transparent'}`} style={{ backgroundColor: c }} aria-label={`Color ${c}`} />
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[13px] font-semibold text-[#0A1551]">Invite by email</label>
              <button onClick={addInvite} data-testid="team-add-invite" className="text-[13px] font-medium text-[#0F766E] hover:text-[#0B5F58] flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" /> Add another
              </button>
            </div>
            <div className="space-y-2">
              {invites.map((m, i) => (
                <div key={m.uid} className="flex gap-2 items-center">
                  <input data-testid={`team-invite-email-${i}`} type="email" value={m.email} onChange={(e) => updateInvite(i, 'email', e.target.value)} placeholder="teammate@company.com"
                    className="flex-1 px-3 py-2 border border-[#D5D7ED] rounded-md text-[13px] focus:outline-none focus:border-[#0A1551]" />
                  <select value={m.role} onChange={(e) => updateInvite(i, 'role', e.target.value)} className="px-2 py-2 border border-[#D5D7ED] rounded-md text-[13px] bg-white focus:outline-none focus:border-[#0A1551]">
                    <option>Admin</option><option>Member</option>
                  </select>
                  {invites.length > 1 && (
                    <button onClick={() => removeInvite(i)} data-testid={`team-invite-remove-${i}`} className="p-2 rounded-md hover:bg-[#FEF2F2] text-[#E5387B]" aria-label="Remove">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[#E3E5F5] flex justify-end gap-2 bg-[#F9FAFD]">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-[#D5D7ED] text-[14px] font-medium text-[#0A1551] hover:bg-white">Cancel</button>
          <button onClick={submit} disabled={loading || !name.trim()} data-testid="team-create-submit"
            className="px-5 py-2 rounded-md bg-[#0F766E] hover:bg-[#0B5F58] text-white text-[14px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
            {loading ? 'Creating...' : 'Create team'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateTeamModal;
